/**
 * Asset summary
 */
export interface AssetSummary {
    title: string | null;
    imagePackId: string | null;
    imageUrl?: string | null;
    raw: unknown;
}

/**
 * Find the first string match in the payload
 * @param payload
 * @param keys
 */
const findFirstStringMatch = (payload: unknown, keys: string[]): string | null => {
    if (!payload || typeof payload !== "object") {
        return null;
    }

    const queue: unknown[] = [payload];

    while (queue.length > 0) {
        const current = queue.shift();
        if (!current || typeof current !== "object") {
            continue;
        }

        if (Array.isArray(current)) {
            queue.push(...current);
            continue;
        }

        for (const [key, value] of Object.entries(current)) {
            if (keys.includes(key) && typeof value === "string") {
                const trimmed = value.trim();
                if (trimmed) {
                    return trimmed;
                }
            }

            if (value && typeof value === "object") {
                queue.push(value);
            }
        }
    }

    return null;
};

/**
 * Ensure non-empty string
 * @param value
 */
const ensureNonEmpty = (value: unknown): string | null => {
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};

/**
 * Fetch asset metadata from the backend
 * @param assetId
 */
export const fetchAssetMetadata = async (assetId: string): Promise<AssetSummary> => {
    const normalizedAssetId = ensureNonEmpty(assetId);

    if (!normalizedAssetId) {
        throw new Error("Asset ID is required to fetch asset metadata.");
    }

    const response = await fetch("/api/Asset&Image/asset", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            assetId: normalizedAssetId,
        }),
    });

    let payload: unknown = null;

    try {
        payload = await response.json();
    } catch (error) {
        throw new Error("Unable to parse asset metadata response.");
    }

    if (!response.ok) {
        const errorMessage =
            (payload && typeof payload === "object" && "error" in payload
                ? String((payload as { error?: unknown }).error)
                : null) ?? "Failed to fetch asset metadata.";
        throw new Error(errorMessage);
    }

    const title =
        findFirstStringMatch(payload, ["title", "name"]) ??
        findFirstStringMatch(payload, ["Title", "Name"]);
    const imagePackId =
        findFirstStringMatch(payload, ["imagePackId", "image-pack-id", "image-pack"]) ??
        findFirstStringMatch(payload, ["ImagePackId", "ImagePackID"]);
    const imageUrl =
        findFirstStringMatch(payload, ["defaultUrl", "imageUrl"]) ??
        findFirstStringMatch(payload, ["DefaultUrl", "ImageUrl"]);

    return {
        title,
        imagePackId,
        imageUrl,
        raw: payload,
    };
};

/**
 * Build image asset URL
 * @param imagePackId
 * @param options
 */
export const buildImageAssetUrl = (
    imagePackId: string,
    options: {
        width?: number;
        height?: number;
        location?: string;
    } = {},
): string | null => {
    const normalizedImagePackId = ensureNonEmpty(imagePackId);

    if (!normalizedImagePackId) {
        return null;
    }

    const url = new URL(
        `https://sales.image-service.eu-north-1-prod.vmnd.tv/api/v2/img/${encodeURIComponent(normalizedImagePackId)}`,
    );

    if (options.location) {
        url.searchParams.set("location", options.location);
    }

    if (options.width) {
        url.searchParams.set("width", String(options.width));
    }

    if (options.height) {
        url.searchParams.set("height", String(options.height));
    }

    return url.toString();
};
